import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  History as HistoryIcon, Sparkles, Trash2, RotateCcw, Pencil, UserPlus, UserMinus,
  CheckSquare, Square, Paperclip, LockIcon as LockIcon, Eye, RotateCw, Play, Bell, Calendar as CalendarIcon,
  ArrowRight, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { HISTORY_COLORS } from "@/components/ui/HistoryTimeline";
import HistoryPanel from "@/components/common/HistoryPanel";
import UserAvatar from "@/components/ui/UserAvatar";

/**
 * TaskHistoryDialog — read-only timeline of a task's audit events.
 *
 * Fetches `/tasks/{taskId}/history` when opened. Each event is rendered as a
 * timeline row with an icon coloured by event type, the actor name, a
 * relative timestamp, and a short human-readable description of the change.
 *
 * The backend serves events in reverse-chronological order (newest first).
 * We keep that ordering so the top of the list is always "what just
 * happened". A vertical dashed line links every row to visually connect the
 * timeline.
 */
const _fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const STATUS_LABEL = {
  planned: "Planifiée",
  waiting: "En attente",
  in_progress: "En cours",
  to_verify: "À vérifier",
  redo: "À refaire",
  completed: "Terminée",
  closed: "Clôturée",
  late: "En retard",
};

const PRIORITY_LABEL = {
  normal: "Normale",
  moyen: "Moyenne",
  urgent: "Élevée",
  immediat: "Immédiate",
};

// Small helper — render a raw value (string / date / status) in a friendly way.
const _renderValue = (field, val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (field === "priority") return PRIORITY_LABEL[val] || val;
  if (field === "deadline" || field === "execution_date") {
    try { return new Date(val).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return String(val); }
  }
  if (typeof val === "string" && val.length > 60) return `${val.slice(0, 60)}…`;
  return String(val);
};

// Per-event visual descriptor. Colour + icon + one-liner text builder.
const EVENT_STYLE = {
  created:               { Icon: Sparkles,   color: "#22C55E", label: () => "Tâche créée" },
  deleted:               { Icon: Trash2,     color: "#EF4444", label: (p) => p?.reason ? `Tâche supprimée — motif : ${p.reason}` : "Tâche supprimée" },
  restored:              { Icon: RotateCcw,  color: "#22C55E", label: () => "Tâche restaurée depuis la corbeille" },
  status_changed:        { Icon: ArrowRight, color: "#0078D4", label: (p) => `Statut : ${STATUS_LABEL[p?.from] || p?.from || "?"} → ${STATUS_LABEL[p?.to] || p?.to || "?"}` },
  assignee_status_changed: { Icon: Play,     color: "#D85A30", label: (p) => `Statut individuel : ${STATUS_LABEL[p?.from] || p?.from} → ${STATUS_LABEL[p?.to] || p?.to}${p?.trigger === "first_open" ? " (première ouverture)" : ""}` },
  completed:             { Icon: CheckSquare, color: "#22C55E", label: (p) => p?.reason ? `Marquée terminée — commentaire : ${p.reason}` : "Marquée terminée" },
  to_verify:             { Icon: Eye,        color: "var(--status-cloture-tasks)", label: (p) => p?.trigger === "all_executors_done" ? "Passée en « À vérifier » (tous les exécuteurs ont terminé)" : "Passée en « À vérifier »" },
  redo:                  { Icon: RotateCw,   color: "#F97316", label: (p) => `Renvoyée à refaire — motif : ${p?.reason || "—"}` },
  relance:               { Icon: Bell,       color: "#E0569B", label: (p) => `Relance n° ${p?.n ?? "?"} envoyée à ${p?.recipients ?? "?"} assigné(s)${p?.message ? ` — « ${p.message} »` : " — sans message"}` },
  closed:                { Icon: LockIcon,       color: "var(--status-cloture-tasks)", label: (p) => p?.reason ? `Clôturée — note : ${p.reason}` : "Clôturée définitivement" },
  field_changed:         { Icon: Pencil,     color: "#5A9EE0", label: (p) => `${p?.label || p?.field || "Champ"} : ${_renderValue(p?.field, p?.old)} → ${_renderValue(p?.field, p?.new)}` },
  assignee_added:        { Icon: UserPlus,   color: "#22C55E", label: (p) => `Assigné ajouté (${STATUS_LABEL[p?.initial_status] || p?.initial_status || "—"})` },
  assignee_removed:      { Icon: UserMinus,  color: "#EF4444", label: () => "Assigné retiré" },
  checklist_added:       { Icon: CheckSquare, color: "#22C55E", label: (p) => `Checklist : « ${p?.label} » ajouté` },
  checklist_removed:     { Icon: Square,     color: "#EF4444", label: (p) => `Checklist : « ${p?.label} » retiré` },
  checklist_toggled:     { Icon: CheckSquare, color: "#0078D4", label: (p) => `Checklist : « ${p?.label} » ${p?.done ? "coché" : "décoché"}` },
  attachment_added:      { Icon: Paperclip,  color: "#0078D4", label: (p) => `Pièce jointe ajoutée : ${p?.filename || "—"}` },
  attachment_removed:    { Icon: Paperclip,  color: "#EF4444", label: (p) => `Pièce jointe retirée : ${p?.filename || "—"}` },
  reminders_changed:     { Icon: Bell,       color: "#F5C842", label: (p) => `Rappels : ${p?.from ?? 0} → ${p?.to ?? 0}` },
  recurrence_changed:    { Icon: CalendarIcon, color: "#7C3AED", label: (p) => p?.enabled ? "Récurrence activée / mise à jour" : "Récurrence désactivée" },
  // Spec iter135 (Feb 2026) — événement de réouverture. Le motif (si
  // renseigné) est rendu en badge violet à côté de la ligne principale
  // via le champ ``extra`` (voir la boucle de rendu ci-dessous).
  reopened: {
    Icon: RotateCcw,
    color: "var(--status-cloture-tasks, #8B5CF6)",
    label: () => "Tâche rouverte",
    extra: (p) => (p?.reason || "").trim() || null,
  },
};

const _fallback = { Icon: AlertTriangle, color: "#9CA3AF", label: (p) => `Événement : ${JSON.stringify(p)?.slice(0, 60)}` };

/** Libellé lisible d'un événement (partagé avec l'infobulle « Activité »). */
export function taskEventLabel(e) {
  const st = EVENT_STYLE[e?.type] || _fallback;
  try { return st.label(e?.payload || {}); } catch { return e?.type || "Événement"; }
}

// iter166 — badges standard (couleurs HISTORY_COLORS).
const TASK_BADGE = {
  created: ["CRÉÉ", HISTORY_COLORS.green], restored: ["RESTAURÉE", HISTORY_COLORS.green], completed: ["TERMINÉE", HISTORY_COLORS.green],
  to_verify: ["À VÉRIFIER", HISTORY_COLORS.violet], closed: ["CLÔTURÉ", HISTORY_COLORS.blue], redo: ["À REFAIRE", HISTORY_COLORS.red],
  reopened: ["RÉOUVERTE", HISTORY_COLORS.red], deleted: ["SUPPRIMÉ", HISTORY_COLORS.red], relance: ["RELANCE", "#E0569B"],
  attachment_removed: ["FICHIER RETIRÉ", HISTORY_COLORS.red], assignee_removed: ["RETIRÉ", HISTORY_COLORS.red], checklist_removed: ["RETIRÉ", HISTORY_COLORS.red],
  attachment_added: ["AJOUTÉ", HISTORY_COLORS.green], assignee_added: ["AJOUTÉ", HISTORY_COLORS.green], checklist_added: ["AJOUTÉ", HISTORY_COLORS.green],
  status_changed: ["STATUT", HISTORY_COLORS.blue], assignee_status_changed: ["STATUT", HISTORY_COLORS.blue],
};

export default function TaskHistoryDialog({ open, onOpenChange, taskId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !taskId) return;
    let alive = true;
    setLoading(true);
    api.get(`/tasks/${taskId}/history`)
      .then(({ data }) => { if (alive) setEvents(data || []); })
      .catch(() => { if (alive) setEvents([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, taskId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-vertec-borderDark max-w-2xl" data-testid="task-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HistoryIcon size={18} className="text-vertec-blue" />
            Historique de la tâche
            <span className="text-xs font-normal text-muted-foreground ml-1">
              · {events.length} événement{events.length > 1 ? "s" : ""}
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center">Chargement…</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center">
            Aucun événement enregistré pour cette tâche.
          </div>
        ) : (
          <div className="max-h-[65vh] overflow-y-auto pr-1" data-testid="task-history-list">
            <HistoryPanel entityType="task" entityId={taskId} entityLabel="la tâche" height={480} testId="task-history" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
